import axios from 'axios';


const API_URL = process.env.REACT_APP_API_URL;

// Verificação de segurança para garantir que a variável de ambiente esteja definida
if (!API_URL) {
  console.error('A variável de ambiente REACT_APP_API_URL não está definida');
}

/**
 * Função para obter o token de autenticação armazenado.
 * @returns {string|null} O token JWT armazenado no localStorage ou null se não existir.
 */
const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * Instância do Axios configurada com a base URL e interceptores
 */
const api = axios.create({
  baseURL: API_URL,
  timeout: 60000, // 1 minuto
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json', // Adicionado header Accept
  },
  withCredentials: true, // Enviar cookies com as requisições
});

/**
 * Interceptor para adicionar o token de autenticação a todas as requisições
 */
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // **Removido:** O cabeçalho `Access-Control-Allow-Credentials` não deve ser definido no cliente
    // Este cabeçalho deve ser definido pelo servidor nas respostas CORS
    // config.headers['Access-Control-Allow-Credentials'] = true;

    // Logger da configuração da requisição
    console.log('Request Config:', {
      url: config.url,
      method: config.method,
      headers: config.headers,
      withCredentials: config.withCredentials,
    });

    return config;
  },
  (error) => {
    console.error('Erro na requisição:', error);
    return Promise.reject(error);
  }
);

/**
 * Interceptor para respostas com tratamento de erros
 */
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.code === 'ECONNABORTED') {
      console.error('A requisição excedeu o tempo limite.', error);
    }

    if (error.response) {
      // Erro do servidor
      if (error.response.status === 401) {
        // Token expirado ou inválido
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      console.error('Erro na resposta da API:', {
        status: error.response.status,
        data: error.response.data,
      });
    } else if (error.request) {
      console.error('Nenhuma resposta recebida:', error.request);
    } else {
      console.error('Erro na configuração da requisição:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * Função para buscar destaques de uma instituição específica.
 * @param {string|number} instituicaoId - ID da instituição
 * @param {string} modo - 'publico' ou 'privado'
 * @returns {Promise<Array>} Lista de destaques
 */
export const getDestaques = async (instituicaoId, modo = 'privado') => {
  try {
    const endpoint =
      modo === 'publico'
        ? `/destaques/instituicao/${instituicaoId}/publico`
        : `/destaques/instituicao/${instituicaoId}`;

    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar destaques:', error);
    throw error;
  }
};

/**
 * Função para buscar um destaque específico.
 * @param {string|number} destaqueId - ID do destaque
 * @returns {Promise<Object>} Dados do destaque
 */
export const getDestaque = async (destaqueId) => {
  try {
    const response = await api.get(`/destaques/${destaqueId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar destaque:', error);
    throw error;
  }
};

/**
 * Função para criar um novo destaque com upload de imagem.
 * @param {string|number} instituicaoId - ID da instituição
 * @param {FormData} formData - Dados do destaque incluindo imagem
 * @returns {Promise<Object>} O destaque criado
 */
export const createDestaque = async (instituicaoId, formData) => {
  try {
    // Garantir que o instituicaoId seja incluído no formData
    if (instituicaoId) {
      formData.append('instituicaoId', instituicaoId);
    }

    const response = await api.post('/destaques', formData, {
      headers: {
        // O Axios irá automaticamente definir o Content-Type correto para FormData
        // 'Content-Type': 'multipart/form-data' é adicionado automaticamente
      },
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao criar destaque:', error);
    throw error;
  }
};

/**
 * Função para atualizar um destaque existente.
 * @param {string|number} destaqueId - ID do destaque a ser atualizado
 * @param {FormData} formData - Dados atualizados do destaque, incluindo possível nova imagem
 * @returns {Promise<Object>} O destaque atualizado
 */
export const updateDestaque = async (destaqueId, formData) => {
  try {
    const response = await api.put(`/destaques/${destaqueId}`, formData, {
      headers: {
        // O Axios irá automaticamente definir o Content-Type correto para FormData
        // 'Content-Type': 'multipart/form-data' é adicionado automaticamente
      },
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar destaque:', error);
    throw error;
  }
};

/**
 * Função para deletar um destaque.
 * @param {string|number} instituicaoId - ID da instituição
 * @param {string|number} destaqueId - ID do destaque a ser deletado
 * @returns {Promise<void>}
 */
export const excluirDestaque = async (instituicaoId, destaqueId) => {
  try {
    await api.delete(`/destaques/instituicao/${instituicaoId}/${destaqueId}`);
  } catch (error) {
    console.error('Erro ao excluir destaque:', error);
    throw error;
  }
};

/**
 * Função para obter a URL completa de uma imagem.
 * @param {string} imagemUrl - URL relativa da imagem
 * @returns {string} URL completa da imagem
 */
export const getImagemUrl = (imagemUrl) => {
  if (!imagemUrl) return '';
  if (imagemUrl.startsWith('http')) return imagemUrl;
  return `${API_URL}/${imagemUrl}`;
};

// Definir o objeto de serviço com todas as funções
const destaqueService = {
  getDestaques,
  getDestaque,
  createDestaque,
  updateDestaque,
  excluirDestaque,
  getImagemUrl,
};

// Exportar o serviço
export default destaqueService;
